import { useEffect, useState } from 'react'
import { fetchContentFulData } from '@helpers/contentful'
import trackError from '@helpers/trackError'

type ImageCard = {
  title: string
  file: {
    url: string
    details: {
      size: number
      image: {
        width: number
        height: number
      }
    }
    fileName: string
    contentType: string
  }
}

export type SizingWidgetItem = {
  title: string
  description: string
  imageMobileHero: ImageCard
  imageCard: ImageCard
  imageDesktopHero: ImageCard
  type: string
}

export type SizingWidgetBlock = {
  productType: string
  sizingItems: SizingWidgetItem[]
  defaultBgColor: string
  hoverBgColor: string
  transitionBgColor: string
  byline: string
  type: string
  excludeProductTypes: Record<string, string>
}

type SizingWidgetBlocks = Record<string, SizingWidgetBlock>

export type SizingWidgetData = {
  title: string
  byline: string
  linkText?: string
  linkUrl?: string
  sizingBlocks: SizingWidgetBlocks
  excludeProductTypes: ArrayLike<string> | null
}

const hasImages = (sizingBlocks: SizingWidgetBlocks | undefined = {}) => {
  let imageExists = true
  const allData = Object.values(sizingBlocks).map(item =>
    Object.values(item.sizingItems)
  )
  allData
    .flatMap(data => data)
    .forEach(value => {
      if (
        !value.imageDesktopHero.file.url ||
        !value.imageMobileHero.file.url ||
        !value.imageCard.file.url
      ) {
        imageExists = false
      }
    })
  return imageExists
}

type UseSizingWidgetArgs = {
  isPreview: boolean
  handle: string
  productCategory?: string
}

export default function useSizingWidgetData({
  isPreview,
  handle,
  productCategory
}: UseSizingWidgetArgs) {
  const [sizingWidgetData, setSizingWidgetData] =
    useState<SizingWidgetData | null>(null)

  useEffect(() => {
    const fetchSizingWidgetData = async () => {
      try {
        const data = (await fetchContentFulData({
          id: '3Y2dy1zyiarkO6N2jADqra',
          depth: 10,
          unNest: false,
          isPreview
        })) as SizingWidgetData

        setSizingWidgetData(data)
      } catch (error) {
        trackError(new Error('Sizing Widget failed'), { error: error as Error })
      }
    }
    fetchSizingWidgetData()
  }, [isPreview])

  if (!hasImages(sizingWidgetData?.sizingBlocks)) {
    trackError(new Error('Sizing Widget has no images'), {
      handle,
      productCategory
    })
    return { sizingWidgetData: {} }
  }

  return sizingWidgetData ? { sizingWidgetData } : { sizingWidgetData: {} }
}
